import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Spade from "@/components/Spade";
import { useLight } from "@/context/LightContext";
import {
    ArrowUpRight,
    ArrowLeft,
    Instagram,
    Building2,
    Landmark,
    Briefcase,
    Users,
    Home as HomeIcon,
    Sparkles,
    Send,
} from "lucide-react";

const WA_URL =
    "https://wa.me/919717622852?text=" +
    encodeURIComponent(
        "Hi, I'm a real estate agent and I want to book my $79 Aestherium trial week.",
    );
const IG_URL = "https://instagram.com/aestherium.media";

// ---------- Content data ----------
const PROBLEMS = [
    {
        title: "You're paying for leads three other agents already have.",
        body: "Every portal lead you buy gets sold to the agents next to you. You're not selling yourself. You're racing a stopwatch. The one who dials fastest wins, and the client never knew your name before the call.",
    },
    {
        title: "Your listings get posted. They don't get seen.",
        body: "You shoot the property, write the caption, hit post. Two hundred views, mostly other agents. The buyers in your own city never saw it.",
    },
    {
        title: "Your reputation stops at your sphere.",
        body: "Referrals are great until they run out. If the only people who know you are people who already know you, your pipeline has a ceiling and you can feel it.",
    },
];

const CASH = [
    {
        letter: "C",
        title: "Content",
        body: "Listing walkthroughs, neighbourhood breakdowns, market updates, buyer and seller education. We build content around what people in your market are actually deciding, not what looks good on a feed.",
    },
    {
        letter: "A",
        title: "Amplify",
        body: "Organic distribution that puts your listings in front of local buyers and sellers, not other agents. Reach that lands in your postcode, not in the void.",
    },
    {
        letter: "S",
        title: "Sell",
        body: "Views become showing requests. Followers become seller enquiries. Your profile, bio, highlights and DMs are rebuilt into a funnel that converts attention into appointments.",
    },
    {
        letter: "H",
        title: "Hook",
        body: "Most people are not ready to transact today. Retention systems keep you in front of them for the six months until they are, so when the moment comes, you are the only name they think of.",
    },
];

const AUDIENCES = [
    {
        icon: HomeIcon,
        title: "Residential Agents",
        body: "More listings, more buyer-side deals, less time chasing cold leads.",
    },
    {
        icon: Sparkles,
        title: "Luxury Listing Specialists",
        body: "Positioning that matches the price point you're asking sellers to trust you with.",
    },
    {
        icon: Landmark,
        title: "Commercial Brokers",
        body: "Reach the investors and business owners who are actually in the market.",
    },
    {
        icon: Users,
        title: "Brokerages & Teams",
        body: "Build a brand your agents recruit into and your clients recognise.",
    },
    {
        icon: Building2,
        title: "Property Managers",
        body: "Fill units faster with inbound enquiries instead of listing-site fees.",
    },
    {
        icon: Briefcase,
        title: "New Agents",
        body: "No sphere yet? Content builds one faster than door-knocking ever will.",
    },
];

const STEPS = [
    {
        n: "01",
        title: "Free Audit",
        body: "We look at your profile, your market, and your current content, and show you exactly where your visibility is leaking.",
    },
    {
        n: "02",
        title: "Trial Week. $79.",
        body: "We run the full system on your account for seven days. You watch the numbers move in real time.",
    },
    {
        n: "03",
        title: "Weekly Partnership",
        body: "If it works, we continue. Simple weekly billing, no long contracts, no lock-in.",
    },
    {
        n: "04",
        title: "Market Ownership",
        body: "We compound it. More reach, more enquiries, until you're the agent people name without thinking.",
    },
];

const FAQS = [
    {
        q: "Do I need to show my face?",
        a: "It helps, but no. We have grown accounts that never show a face. Property, market data and voiceover carry more than people expect, and we build around whatever you're actually comfortable doing.",
    },
    {
        q: "My market is small. Does this still work?",
        a: "A small market is an advantage. There is less competition for attention and you need far fewer people to see you before you're the obvious choice. We target reach to your area rather than chasing national numbers that never buy from you.",
    },
    {
        q: "How is this different from buying leads?",
        a: "A bought lead is rented and shared. Organic reach is owned and exclusive. When someone messages you from your own content, they already know who you are and they chose you. That conversation starts in a completely different place.",
    },
    {
        q: "Do you post for me, or do I?",
        a: "We handle it. Strategy, content direction, captions, hooks, posting, engagement, reporting. You keep doing what you're good at, which is talking to clients and closing.",
    },
    {
        q: "What happens in the trial week?",
        a: "The real work, not a demo. Audit, strategy, profile rebuild, content plan, and the distribution system running live on your account. You see actual movement in seven days.",
    },
    {
        q: "What if it doesn't work for me?",
        a: "Then you don't continue. There's no contract holding you. That's exactly why the trial is priced where it is.",
    },
];

const CHIPS = ["190+ Brands Scaled", "200M+ Reach in 7 Days", "Zero Ad Spend"];

// ---------- Component ----------
export default function RealEstate() {
    const { activate, setTheme } = useLight();

    // Green room mounts already illuminated — no second light ritual here.
    // Keep the lighting system live (spotlight cursor + ambient glow + card
    // edge-catch) by calling activate() on mount so `data-lit` is set on <html>.
    useEffect(() => {
        setTheme("green");
        const cx = typeof window !== "undefined" ? window.innerWidth / 2 : 0;
        const cy = typeof window !== "undefined" ? window.innerHeight / 3 : 0;
        activate(cx, cy);
        window.scrollTo(0, 0);
        return () => setTheme("gold");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // SEO metadata
    useEffect(() => {
        const prevTitle = document.title;
        document.title = "Instagram Marketing for Real Estate Agents — Aestherium Media";
        const desc =
            "Organic Instagram growth for real estate agents, brokers, luxury specialists and brokerages. Stop buying shared leads — own your market's attention. $79 trial week, zero ad spend.";
        const keywords =
            "instagram marketing for real estate agents, organic social media for realtors, real estate agent instagram growth, realtor content marketing, aestherium media real estate";

        const setMeta = (name, content, attr = "name") => {
            let m = document.querySelector(`meta[${attr}="${name}"]`);
            if (!m) {
                m = document.createElement("meta");
                m.setAttribute(attr, name);
                document.head.appendChild(m);
            }
            m.setAttribute("content", content);
            return m;
        };
        const created = [
            setMeta("description", desc),
            setMeta("keywords", keywords),
            setMeta("og:title", "Aestherium — Real Estate Instagram Growth", "property"),
            setMeta("og:description", desc, "property"),
            setMeta("og:type", "website", "property"),
        ];
        return () => {
            document.title = prevTitle;
            created.forEach((m) => m?.remove());
        };
    }, []);

    return (
        <div
            className="min-h-screen text-[var(--text-primary)] relative"
            data-testid="real-estate-page"
        >
            {/* Back link — never strand the user */}
            <div className="absolute top-6 left-6 z-40">
                <Link
                    to="/"
                    data-testid="real-estate-back"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-subtle)] text-[var(--accent-champagne)]/80 hover:text-[var(--accent-champagne)] hover:border-[var(--border-accent)] backdrop-blur-md bg-black/25 text-xs uppercase tracking-[0.2em]"
                >
                    <ArrowLeft size={13} /> Back through the door
                </Link>
            </div>

            {/* ==================== HERO ==================== */}
            <section className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 md:pt-32 pb-16">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-gradient-to-b from-[#017A48] via-[#006039] to-transparent" />
                    <div className="absolute inset-0 opacity-50 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.15),transparent_60%)]" />
                    <div className="absolute right-[-6%] top-[8%] opacity-[0.08] rotate-[8deg]">
                        <Spade size={520} />
                    </div>
                    <div className="absolute left-[-4%] bottom-[-10%] opacity-[0.05] -rotate-[12deg]">
                        <Spade size={380} />
                    </div>
                </div>

                <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 relative">
                    <div data-lit-target>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-xs md:text-sm uppercase tracking-[0.35em] text-[var(--accent-champagne)]/80 mb-6"
                        >
                            For Agents, Brokers & Brokerages
                        </motion.p>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.15 }}
                            className="font-display font-bold leading-[1.05] tracking-tight text-[var(--text-primary)] wordmark-emboss"
                            style={{ fontSize: "clamp(2rem, 6.5vw, 5rem)" }}
                        >
                            Stop Buying Leads.{" "}
                            <span className="italic text-[var(--accent-champagne)]">
                                Start Being The Reason They Call.
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.35 }}
                            className="mt-6 md:mt-8 text-lg md:text-xl text-[var(--text-secondary)] max-w-3xl leading-relaxed"
                        >
                            Aestherium turns a real estate agent's Instagram into a listing machine.
                            Buyers and sellers in your market find you, watch you, and message you
                            first. Organically.{" "}
                            <span className="text-[var(--accent-champagne)]">No paid ads.</span> No
                            shared leads. No cold calls.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.5 }}
                            className="mt-8 flex flex-wrap gap-3"
                        >
                            {CHIPS.map((chip) => (
                                <span
                                    key={chip}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-accent)] bg-[rgba(212,175,55,0.06)] text-[var(--accent-champagne)] text-xs md:text-sm uppercase tracking-[0.18em]"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-gold)] soft-pulse" />
                                    {chip}
                                </span>
                            ))}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.7 }}
                            className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                        >
                            <a
                                href={WA_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="re-hero-cta-trial"
                                className="group inline-flex items-center gap-3 pl-6 pr-4 py-4 bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm rounded-full hover:bg-[var(--accent-champagne)] transition-all shadow-[0_20px_40px_-15px_rgba(212,175,55,0.6)] hover:translate-y-[-2px]"
                            >
                                Book Your $79 Trial Week
                                <span className="w-9 h-9 rounded-full bg-[var(--brand-dark)] flex items-center justify-center group-hover:rotate-45 transition-transform">
                                    <ArrowUpRight size={16} className="text-[var(--accent-gold)]" />
                                </span>
                            </a>
                            <a
                                href={IG_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="re-hero-cta-ig"
                                className="inline-flex items-center gap-2 px-6 py-4 border border-[var(--border-accent)] text-[var(--text-primary)] rounded-full text-sm uppercase tracking-[0.2em] hover:bg-white/5 transition-all"
                            >
                                <Instagram size={16} /> DM us on Instagram
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ==================== PROBLEM ==================== */}
            <Section eyebrow="The Honest Part" heading="You're not short on effort. You're short on visibility.">
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                    {PROBLEMS.map((p, i) => (
                        <Card key={i} index={i}>
                            <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] leading-tight mb-4">
                                {p.title}
                            </h3>
                            <p className="text-[var(--text-secondary)] leading-relaxed text-[15px]">
                                {p.body}
                            </p>
                        </Card>
                    ))}
                </div>
            </Section>

            {/* ==================== SHIFT ==================== */}
            <Section eyebrow="What Changes" heading="The agent who owns attention owns the market.">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="text-[var(--text-secondary)] text-lg md:text-xl leading-relaxed max-w-4xl"
                >
                    In every market there is one agent whose name comes up first. Not because they
                    are better at closing. Because when someone finally decides to buy or sell,
                    that agent is already in their head. That position is not luck and it is not a
                    budget. It is built, week over week, with content that actually travels.{" "}
                    <span className="text-[var(--accent-champagne)]">
                        That is the entire job we do for you.
                    </span>
                </motion.p>
            </Section>

            {/* ==================== CASH ==================== */}
            <Section
                eyebrow="The Method"
                heading="The C.A.S.H Strategy, built for property."
            >
                <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                    {CASH.map((c, i) => (
                        <Card key={c.letter} index={i}>
                            <div className="flex items-baseline gap-4 mb-4">
                                <span className="font-display font-bold text-6xl md:text-7xl gold-shimmer leading-none">
                                    {c.letter}
                                </span>
                                <span className="text-xs uppercase tracking-[0.3em] text-[var(--accent-champagne)]/80">
                                    {c.title}
                                </span>
                            </div>
                            <p className="text-[var(--text-secondary)] leading-relaxed">{c.body}</p>
                        </Card>
                    ))}
                </div>
            </Section>

            {/* ==================== AUDIENCES ==================== */}
            <Section heading="Built for people who close, not people who post.">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {AUDIENCES.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <Card key={a.title} index={i} compact>
                                <div className="w-11 h-11 rounded-full border border-[var(--border-accent)] flex items-center justify-center mb-4 text-[var(--accent-gold)]">
                                    <Icon size={18} />
                                </div>
                                <h3 className="font-display text-lg md:text-xl text-[var(--text-primary)] mb-2">
                                    {a.title}
                                </h3>
                                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                                    {a.body}
                                </p>
                            </Card>
                        );
                    })}
                </div>
            </Section>

            {/* ==================== MATH ==================== */}
            <Section eyebrow="The Math" heading="One closing pays for a year of this.">
                <div className="max-w-4xl">
                    <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-6">
                        You already know what a single commission is worth in your market. Now
                        compare that to what you spend every month on portal leads that three other
                        agents are also calling. We are not asking you to spend more.{" "}
                        <span className="text-[var(--accent-champagne)]">
                            We are asking you to stop renting attention and start owning it.
                        </span>
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]/80 italic border-l-2 border-[var(--accent-gold)]/60 pl-5 leading-relaxed">
                        We don't guarantee deals, and any agency that does is lying to you. What we
                        guarantee is the work: real strategy, real distribution, real reporting,
                        every single week.
                    </p>
                </div>
            </Section>

            {/* ==================== PROOF ==================== */}
            <div data-testid="re-proof">
                <Section eyebrow="Proof, Not Promises" heading="Real estate case studies land here first.">
                    <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-3xl mb-4">
                        We&apos;re documenting the numbers with our first cohort of real
                        estate clients right now. As each week&apos;s reach, enquiries and
                        showings compound, the receipts will be posted here — raw and
                        unedited, the same way we do it on the main site.
                    </p>
                    <p className="text-sm text-[var(--accent-champagne)]/80 max-w-3xl italic">
                        Want to be one of the case studies people quote in six months?
                        Take the trial week.
                    </p>
                </Section>
                {/* Placeholder slots for future real-estate case studies */}
                <Section>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((n) => (
                            <div
                                key={n}
                                data-testid={`re-case-placeholder-${n}`}
                                className="surface-3d rounded-2xl border border-dashed border-[var(--border-subtle)] p-8 min-h-[220px] flex flex-col justify-center items-center text-center opacity-70"
                            >
                                <span className="text-[10px] uppercase tracking-[0.35em] text-[var(--accent-champagne)]/60 mb-3">
                                    Case Study #{n}
                                </span>
                                <p className="text-[var(--text-secondary)] text-sm">
                                    Real estate client result — documenting now.
                                </p>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>

            {/* ==================== HOW IT WORKS ==================== */}
            <Section heading="From first message to first enquiry.">
                <div className="relative">
                    <div className="absolute left-4 md:left-6 top-2 bottom-2 w-px bg-gradient-to-b from-[var(--accent-gold)]/60 via-[var(--border-subtle)] to-transparent" />
                    <div className="space-y-8">
                        {STEPS.map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, x: -16 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.8, delay: i * 0.06 }}
                                className="relative pl-14 md:pl-20"
                            >
                                <span className="absolute left-0 top-0 w-8 h-8 md:w-12 md:h-12 rounded-full bg-[var(--brand-dark)] border border-[var(--border-accent)] flex items-center justify-center text-[var(--accent-gold)] font-display text-xs md:text-sm">
                                    {s.n}
                                </span>
                                <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] mb-2">
                                    {s.title}
                                </h3>
                                <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl">
                                    {s.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ==================== FAQ ==================== */}
            <Section heading="Straight answers.">
                <div className="grid md:grid-cols-2 gap-4 lg:gap-6" data-testid="re-faq">
                    {FAQS.map((f, i) => (
                        <FaqItem key={i} q={f.q} a={f.a} />
                    ))}
                </div>
            </Section>

            {/* ==================== FINAL CTA ==================== */}
            <FinalCta />

            {/* Footer stub */}
            <footer className="border-t border-[var(--border-subtle)] py-10 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                    <Spade size={20} />
                    <span className="font-display text-lg text-[var(--text-primary)]">
                        Aestherium<span className="text-[var(--accent-gold)]">.</span>
                    </span>
                </div>
                <p className="text-[var(--text-secondary)]/60 text-xs uppercase tracking-[0.3em]">
                    Organic Reach. Real Sales. Zero Ads.
                </p>
                <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[var(--accent-champagne)]/80 hover:text-[var(--accent-champagne)]"
                >
                    <ArrowLeft size={12} /> Back through the door
                </Link>
            </footer>
        </div>
    );
}

// ---------- Helpers ----------
function Section({ eyebrow, heading, children }) {
    return (
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {(eyebrow || heading) && (
                    <div className="mb-10 md:mb-16 max-w-4xl">
                        {eyebrow && (
                            <p className="font-fraktur text-[var(--accent-gold)] text-sm tracking-[0.2em] mb-4 opacity-80">
                                {eyebrow}
                            </p>
                        )}
                        {heading && (
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.85 }}
                                className="font-display font-bold text-3xl md:text-5xl leading-tight text-[var(--text-primary)]"
                            >
                                {heading}
                            </motion.h2>
                        )}
                    </div>
                )}
                {children}
            </div>
        </section>
    );
}

function Card({ children, index = 0, compact = false }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: index * 0.06 }}
            data-glow-catch
            className={`tilt-card surface-3d rounded-2xl border border-[var(--border-subtle)] ${
                compact ? "p-6" : "p-7 md:p-8"
            } bg-[rgba(255,255,255,0.02)]`}
        >
            {children}
        </motion.div>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-left surface-3d rounded-2xl border border-[var(--border-subtle)] p-6 hover:border-[var(--border-accent)] transition-colors"
        >
            <div className="flex items-start gap-4">
                <span className="font-display text-2xl text-[var(--accent-gold)] leading-none">
                    {open ? "−" : "+"}
                </span>
                <div className="flex-1">
                    <h4 className="font-display text-lg text-[var(--text-primary)] mb-2">{q}</h4>
                    {open && (
                        <p className="text-[var(--text-secondary)] leading-relaxed text-[15px] mt-2">
                            {a}
                        </p>
                    )}
                </div>
            </div>
        </button>
    );
}

function FinalCta() {
    const [form, setForm] = useState({
        name: "",
        business: "",
        ig_handle: "",
        email: "",
        message: "",
    });
    const [status, setStatus] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await api.post("/leads", { ...form, source: "real-estate" });
            setStatus("sent");
            setForm({ name: "", business: "", ig_handle: "", email: "", message: "" });
        } catch {
            setStatus("error");
        }
    };

    return (
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div
                    data-testid="re-final-cta"
                    className="grid lg:grid-cols-2 gap-10 lg:gap-16 surface-3d rounded-3xl border border-[var(--border-accent)] p-8 md:p-12 lg:p-16"
                >
                    <div>
                        <p className="font-fraktur text-[var(--accent-gold)] text-sm tracking-[0.2em] mb-4 opacity-80">
                            Your Market. Your Move.
                        </p>
                        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                            Own Your Market.
                        </h2>
                        <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-6">
                            You already do the hard part. Give it the system that puts it in front
                            of the people who are actually ready to buy and sell.{" "}
                            <span className="gold-shimmer font-semibold">$79</span>, one week, the
                            real work.
                        </p>
                        <ul className="space-y-3">
                            {[
                                "No card details on this site",
                                "Weekly billing after trial, no long contracts",
                                "Audit and strategy within 24 hours",
                            ].map((b) => (
                                <li
                                    key={b}
                                    className="flex items-center gap-3 text-[var(--text-primary)]"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-gold)]" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <a
                            href={WA_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="re-final-wa"
                            className="mt-8 inline-flex items-center gap-3 pl-6 pr-4 py-4 bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm rounded-full hover:bg-[var(--accent-champagne)] transition-all"
                        >
                            Book My $79 Trial Week
                            <span className="w-9 h-9 rounded-full bg-[var(--brand-dark)] flex items-center justify-center">
                                <ArrowUpRight size={16} className="text-[var(--accent-gold)]" />
                            </span>
                        </a>
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="re-lead-form">
                        {[
                            ["name", "Your Name", "text", true],
                            ["business", "Brokerage / Team", "text", false],
                            ["ig_handle", "Instagram Handle", "text", false],
                            ["email", "Email", "email", true],
                        ].map(([k, label, type, required]) => (
                            <label key={k} className="block">
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-[var(--accent-champagne)]/70 mb-2">
                                    {label}
                                </span>
                                <input
                                    type={type}
                                    required={required}
                                    value={form[k]}
                                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                    className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--accent-gold)] focus:outline-none transition-colors"
                                />
                            </label>
                        ))}
                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.3em] text-[var(--accent-champagne)]/70 mb-2">
                                Notes (optional)
                            </span>
                            <textarea
                                rows={3}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--accent-gold)] focus:outline-none transition-colors"
                                placeholder="Current goals, biggest bottleneck…"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={status === "sending" || status === "sent"}
                            data-testid="re-lead-submit"
                            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-colors disabled:opacity-70"
                        >
                            {status === "sending"
                                ? "Sending…"
                                : status === "sent"
                                  ? "Received. Talk soon."
                                  : "Book my $79 trial"}
                            <Send size={14} />
                        </button>
                        {status === "error" && (
                            <p className="text-sm text-red-300">
                                Something went wrong — DM us on Instagram instead.
                            </p>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
}
